import { getCookiePrefs } from "@/components/CookieConsent";

type Primitive = string | number | boolean;
type GtagFn = (...args: unknown[]) => void;

export type ProductEventName =
  | "signup_completed"
  | "match_saved"
  | "new_match_funnel"
  | "connected_invite_created"
  | "connected_invite_opened"
  | "connected_invite_claimed"
  | "connected_match_confirmed"
  | "connected_performance_report_notification_opened"
  | "play_review_prompt_shown"
  | "play_review_prompt_later"
  | "play_review_prompt_clicked"
  | "install_cta_clicked"
  | "ios_install_help_opened"
  | "ios_standalone_detected"
  | "ios_install_prompt_shown"
  | "ios_install_prompt_dismissed";

const ALLOWED_PARAMS: Record<ProductEventName, readonly string[]> = {
  signup_completed: ["method"],
  match_saved: ["format", "kind", "milestone"],
  new_match_funnel: ["stage"],
  connected_invite_created: ["delivery"],
  connected_invite_opened: [],
  connected_invite_claimed: [],
  connected_match_confirmed: [],
  connected_performance_report_notification_opened: [],
  play_review_prompt_shown: ["source"],
  play_review_prompt_later: ["source"],
  play_review_prompt_clicked: ["source"],
  install_cta_clicked: ["platform", "destination", "source"],
  ios_install_help_opened: ["source"],
  ios_standalone_detected: [],
  ios_install_prompt_shown: [],
  ios_install_prompt_dismissed: [],
};

const DEDUP_PREFIX = "tl-product-analytics:";

/**
 * Product analytics is deliberately stricter than generic GA page measurement:
 * only an explicit event/parameter allowlist can leave the browser.
 */
export function sanitizeProductEventParams(
  name: ProductEventName,
  params: Record<string, unknown> = {},
): Record<string, Primitive> {
  const allowed = new Set(ALLOWED_PARAMS[name]);
  const safe: Record<string, Primitive> = {};

  for (const [key, value] of Object.entries(params)) {
    if (!allowed.has(key)) continue;
    if (typeof value !== "string" && typeof value !== "number" && typeof value !== "boolean") continue;
    if (typeof value === "number" && !Number.isFinite(value)) continue;
    safe[key] = typeof value === "string" ? value.slice(0, 64) : value;
  }
  return safe;
}

export function analyticsConsentGranted(): boolean {
  if (typeof window === "undefined") return false;
  return getCookiePrefs()?.analytics === true;
}

/**
 * Sends a product event only when analytics consent is granted *right now* and
 * GA4 is already available. It never creates dataLayer/gtag and therefore never
 * queues a pre-consent event for later delivery.
 */
export function trackProductEvent(
  name: ProductEventName,
  params: Record<string, unknown> = {},
): boolean {
  try {
    if (!analyticsConsentGranted()) return false;
    const gtag = (window as unknown as { gtag?: GtagFn }).gtag;
    if (typeof gtag !== "function") return false;
    gtag("event", name, sanitizeProductEventParams(name, params));
    return true;
  } catch {
    return false;
  }
}

/**
 * Marks an event as consumed even when consent is absent. That is intentional:
 * granting analytics later must never replay an event that happened pre-consent.
 * The dedup key stays in browser storage and is never included in the GA payload.
 */
export function trackProductEventOnce(
  scope: "session" | "local",
  dedupKey: string,
  name: ProductEventName,
  params: Record<string, unknown> = {},
): boolean {
  if (typeof window === "undefined" || !dedupKey) return false;
  try {
    const storage = scope === "session" ? window.sessionStorage : window.localStorage;
    const key = `${DEDUP_PREFIX}${dedupKey}`;
    if (storage.getItem(key)) return false;
    storage.setItem(key, "1");
    return trackProductEvent(name, params);
  } catch {
    // Storage can be unavailable in hardened/private browsing. Do not sacrifice
    // the event if consent is present; tracking still remains consent-gated.
    return trackProductEvent(name, params);
  }
}

export function matchMilestoneForCount(count: number | null | undefined): 1 | 2 | 5 | null {
  return count === 1 || count === 2 || count === 5 ? count : null;
}

/**
 * userId is used only to build a local dedup key. It is never passed to gtag.
 */
export function trackSignupCompletedOnce(userId: string, method: "email" | "google" | "unknown") {
  if (!userId) return false;
  return trackProductEventOnce("local", `signup:${userId}`, "signup_completed", { method });
}
